// src/tracker/components/AddTransactionDialog.js

import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box, ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNotification } from '../../context/NotificationContext';

const AddTransactionDialog = ({ open, onClose, onSuccess, accounts, transactionToEdit = null, defaultType = 'expense' }) => {
    const { showNotification } = useNotification();
    const phpSymbol = '₱';
    const [type, setType] = useState('expense');
    const [amount, setAmount] = useState('');
    const [category, setCategory] = useState('');
    const [date, setDate] = useState(dayjs());
    const [accountId, setAccountId] = useState('');
    const [description, setDescription] = useState('');
    const [currency, setCurrency] = useState('PHP');

    const isEditing = transactionToEdit !== null;

    useEffect(() => {
        if (open) {
            if (isEditing) {
                const originalAccount = accounts.find(acc => acc._id === transactionToEdit.account_id);
                setType(transactionToEdit.type);
                setAmount(transactionToEdit.amount.toString());
                setCategory(transactionToEdit.category);
                setDate(dayjs(transactionToEdit.date));
                setAccountId(transactionToEdit.account_id);
                setDescription(transactionToEdit.description || '');
                setCurrency(originalAccount ? originalAccount.currency : 'PHP');
            } else {
                setType(defaultType || 'expense'); setAmount(''); setCategory('');
                setDate(dayjs()); setAccountId(''); setDescription(''); setCurrency('PHP');
            }
        }
    }, [open, transactionToEdit, accounts, isEditing, defaultType]);

    useEffect(() => {
        if (accountId) {
            const selectedAccount = accounts.find(acc => acc._id === accountId);
            if (selectedAccount) setCurrency(selectedAccount.currency);
        }
    }, [accountId, accounts]);

    const handleTypeChange = (event, newType) => {
        if (newType !== null) setType(newType);
    };

    const handleSubmit = async () => {
        if (!accountId || !amount || !category) {
            showNotification('Please fill out Amount, Category, and Account.', 'warning');
            return;
        }
        const transactionData = {
            account_id: accountId, type, amount: parseFloat(amount),
            category, date: date.format('YYYY-MM-DD'), description, currency
        };
        const url = isEditing ? `https://frek.cc/proxy/38.54.86.24:8001/api/transactions/${transactionToEdit._id}` : 'https://frek.cc/proxy/38.54.86.24:8001/api/transactions';
        const method = isEditing ? 'PUT' : 'POST';
        try {
            const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(transactionData) });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit transaction');
            }
            showNotification(isEditing ? 'Transaction updated successfully!' : 'Transaction added successfully!', 'success');
            onSuccess();
        } catch (error) {
            console.error(error);
            showNotification(`Error: ${error.message}`, 'error');
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>{isEditing ? 'Edit Transaction' : 'Add New Transaction'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <ToggleButtonGroup color="primary" value={type} exclusive onChange={handleTypeChange}>
                            <ToggleButton value="expense">Expense</ToggleButton>
                            <ToggleButton value="income">Income</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <TextField autoFocus margin="dense" label={`Amount (${currency === 'USD' ? '$' : phpSymbol})`} type="number" fullWidth value={amount} onChange={e => setAmount(e.target.value)} />
                    <TextField margin="dense" label="Category" type="text" fullWidth value={category} onChange={e => setCategory(e.target.value)} />
                    <DatePicker label="Date" value={date} onChange={(newDate) => setDate(newDate)} sx={{ width: '100%', mt: 1 }} />
                    <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
                        <InputLabel>Account</InputLabel>
                        <Select value={accountId} label="Account" onChange={e => setAccountId(e.target.value)}>
                            {accounts.map(acc => (
                                <MenuItem key={acc._id} value={acc._id}>
                                    {acc.name} ({acc.currency === 'USD' ? '$' : phpSymbol}{acc.balance.toLocaleString()})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Currency</InputLabel>
                        <Select value={currency} label="Currency" disabled>
                            <MenuItem value="PHP">PHP ({phpSymbol})</MenuItem>
                            <MenuItem value="USD">USD ($)</MenuItem>
                        </Select>
                        <FormHelperText>Currency is determined by the selected account.</FormHelperText>
                    </FormControl>
                    <TextField margin="dense" label="Description (Optional)" type="text" fullWidth value={description} onChange={e => setDescription(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save Transaction</Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default AddTransactionDialog;
